import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation } from "@apollo/client";

import { day_translate } from "../../../utils/translate";
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Box, Button, Card, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, LinearProgress, MenuItem, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '@mui/material';
import EntityConfirmDelete from '../subcomponents/EntityConfirmDelete';
import { DeleteIcon, EditIcon, StudentIcon, ConflictIcon } from "../../Icons";
import { COPY_ALL_GROUP_ACTIVITIES, CREATE_GROUP_ACTIVITY, LIST_PERIODS } from '../../../graphql/admin-requests';
import CUGroupActivity from "../CreateUpdateComponents/GroupActivity";
import CUModal from '../modals/CUModal';
import moment from "moment";
import UsersByGroupList from '../UserByGroupList/UserByGroupList';
import CustomIconButton from '../subcomponents/CustomIconButton';
import GroupActivityConflicts from '../GroupActivityConflicts';
import { useSnackbar } from 'notistack';
import { castPeriodDate } from "../../../utils/time";

const GroupActivityListResults = ({ readOnly, period, groupActivities, totalCount, setLimit, limit, setOffset, offset, handleDeleteActivity, loadData, ...rest }) => {

    const { enqueueSnackbar } = useSnackbar();

    const [selectedActivityIds, setSelectedActivityIds] = useState([]);
    const [page, setPage] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);
    const [openCopyDialog, setOpenCopyDialog] = useState(false);
    const [toDelete, setToDelete] = useState({});

    const [openEditModal, setOpenEditModal] = useState(false);
    const [activityToUpdate, setActivityToUpdate] = useState({});

    const [confirmed, setConfirmed] = useState(false);

    const [openUserListModal, setOpenUserListModal] = useState(false);
    const [groupUid, setGroupUid] = useState("");

    const [openConflictModal, setOpenConflictModal] = useState(false);
    const [groupActivityUid, setGroupActivityUid] = useState("");

    const [periods, setPeriods] = useState([]);
    const [periodToCopy, setPeriodToCopy] = useState("");

    const showUserListModal = (uid) => {
        setGroupUid(uid);
        setOpenUserListModal(true);
    }

    const showConflictModal = (uid) => {
        setGroupActivityUid(uid);
        setOpenConflictModal(true);
    }

    const handleSelectAll = (event) => {
        let newSelectedActivityIds;

        if (event.target.checked) {
            newSelectedActivityIds = groupActivities.map((activity) => activity.uid);
        } else {
            newSelectedActivityIds = [];
        }

        setSelectedActivityIds(newSelectedActivityIds);
    };

    const handleSelectOne = (event, id) => {
        const selectedIndex = selectedActivityIds.indexOf(id);
        let newSelectedActivityIds = [];

        if (selectedIndex === -1) {
            newSelectedActivityIds = newSelectedActivityIds.concat(selectedActivityIds, id);
        } else if (selectedIndex === 0) {
            newSelectedActivityIds = newSelectedActivityIds.concat(selectedActivityIds.slice(1));
        } else if (selectedIndex === selectedActivityIds.length - 1) {
            newSelectedActivityIds = newSelectedActivityIds.concat(selectedActivityIds.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelectedActivityIds = newSelectedActivityIds.concat(
                selectedActivityIds.slice(0, selectedIndex),
                selectedActivityIds.slice(selectedIndex + 1)
            );
        }

        setSelectedActivityIds(newSelectedActivityIds);
    };

    const handleLimitChange = async (event) => {
        await setLimit(event.target.value);
    };

    const openConfirmDelete = (activity) => {
        setToDelete(activity);
        setOpenDialog(true);
    }

    const closeDialog = () => setOpenDialog(false);
    const closeCopyDialog = () => setOpenCopyDialog(false);

    const handlePageChange = async (event, newPage) => {
        await setOffset(newPage * limit);
        await setPage(newPage);
    };

    const [copyAllGroupActivities] = useMutation(COPY_ALL_GROUP_ACTIVITIES, {
        onCompleted: (data) => {
            const nb = data.copyAllGroupActivities.integer;
            enqueueSnackbar(`${nb} options copiées`, { variant: "success" });
            return Promise.resolve("Options copiées");
        },
        onError: (error) => {
            enqueueSnackbar("Impossible de copier les options", { variant: "error" })
            return Promise.reject("Impossible de copier les options");
        },
    });

    const [createGroupActivity] = useMutation(CREATE_GROUP_ACTIVITY, {
        onError: (error) => {
            console.error("Error creating group activity", error);
        },
    });

    const confirmCopyAll = async () => {
        setConfirmed(true);
        await copyAllGroupActivities({ variables: { sourcePeriodUid: period.uid, targetPeriodUid: periodToCopy } });
        loadData();
        closeCopyDialog();
        setConfirmed(false);
    }

    const confirmCopySelected = async () => {
        setConfirmed(true);
        try {
            const selectedActivities = groupActivities.filter(activity =>
                selectedActivityIds.includes(activity.uid)
            );

            let successCount = 0;
            for (const activity of selectedActivities) {
                try {
                    await createGroupActivity({
                        variables: {
                            day: activity.day,
                            name: activity.name,
                            startTime: activity.startTime,
                            endTime: activity.endTime,
                            organizerUid: activity.userByOrganizerUid?.uid,
                            placeUid: activity.placeUid,
                            groupUid: activity.groupByGroupUid?.uid,
                            periodUid: periodToCopy
                        }
                    });
                    successCount++;
                } catch (error) {
                    console.error(`Error copying activity ${activity.name}`, error);
                }
            }

            enqueueSnackbar(`${successCount} option${successCount > 1 ? 's copiées' : ' copiée'}`, { variant: "success" });
            setSelectedActivityIds([]);
            loadData();
        } catch (error) {
            enqueueSnackbar("Erreur lors de la copie des options", { variant: "error" });
        } finally {
            closeCopyDialog();
            setConfirmed(false);
        }
    }

    const [listPeriods] = useLazyQuery(LIST_PERIODS, {
        onCompleted: (data) => setPeriods(data.allPeriods.nodes),
        onError: (error) => console.error("Error requesting list of periods", error),
        fetchPolicy: 'cache-and-network'
    });


    useEffect(() => {
        // Retrieve all periods
        if (openCopyDialog) { listPeriods(); }
    }, [openCopyDialog, listPeriods]);

    return (
        <div>
            <CUModal open={openUserListModal} groupUid={groupUid} handleClose={() => setOpenUserListModal(false)} component={UsersByGroupList} />
            <CUModal open={openConflictModal} groupActivityUid={groupActivityUid} handleClose={() => setOpenConflictModal(false)} component={GroupActivityConflicts} />
            <EntityConfirmDelete
                open={openDialog}
                item={toDelete}
                itemLabel="une activité"
                onClose={closeDialog}
                onConfirm={() => handleDeleteActivity(toDelete.uid)}
            />
            <CUModal open={openEditModal} component={CUGroupActivity} handleClose={() => { setOpenEditModal(false); loadData() }} toUpdate={activityToUpdate} />
            {openCopyDialog &&
                <Dialog
                    open={openCopyDialog}
                    keepMounted
                    onClose={closeCopyDialog}
                >
                    <DialogTitle>Copier les options</DialogTitle>
                    <DialogContent>
                        {confirmed ?
                            <LinearProgress variant="indeterminate" /> :
                            <>
                                <DialogContentText>
                                    Selectionnez la période dans laquelle vous souhaitez copier{' '}
                                    {selectedActivityIds.length === totalCount ? (
                                        <strong>toutes les options ({totalCount})</strong>
                                    ) : (
                                        <strong>{selectedActivityIds.length} option{selectedActivityIds.length > 1 ? 's' : ''} sélectionnée{selectedActivityIds.length > 1 ? 's' : ''}</strong>
                                    )}.<br />
                                </DialogContentText>

                                <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                                    <InputLabel id="period">Periode</InputLabel>
                                    <Select
                                        fullWidth={true}
                                        labelid="period"
                                        label="Période"
                                        id="period"
                                        value={periodToCopy}
                                        onChange={(event) => setPeriodToCopy(event.target.value)}
                                    >
                                        {periods.map(p => <MenuItem key={p.uid} value={p.uid}>{p.name} ({castPeriodDate(p.startDate)} - {castPeriodDate(p.endDate)})</MenuItem>)}

                                    </Select>
                                </FormControl>
                            </>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeCopyDialog} color="primary" disabled={confirmed}>Annuler</Button>
                        <Button 
                            onClick={selectedActivityIds.length === totalCount ? confirmCopyAll : confirmCopySelected} 
                            color="primary"
                            disabled={confirmed || !periodToCopy}
                        >
                            {confirmed ? 'Copie en cours...' : 'Confirmer'}
                        </Button>
                    </DialogActions>
                </Dialog>}
            <Card sx={{ display: { xs: 'none', lg: 'block' } }} {...rest}>
                {!readOnly && selectedActivityIds.length > 0 && (
                    <Box sx={{ p: 2 }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setOpenCopyDialog(true)}
                        >
                            {selectedActivityIds.length === totalCount 
                                ? `Copier toutes les options (${totalCount})`
                                : `Copier ${selectedActivityIds.length} option${selectedActivityIds.length > 1 ? 's' : ''}`
                            }
                        </Button>
                    </Box>
                )}
                <PerfectScrollbar>
                    <Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedActivityIds.length > 0 && selectedActivityIds.length === groupActivities.length}
                                            color="primary"
                                            indeterminate={
                                                selectedActivityIds.length > 0
                                                && selectedActivityIds.length < groupActivities.length
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell size="small">Nom</TableCell>
                                    <TableCell size="small">Jour</TableCell>
                                    <TableCell size="small">Heures</TableCell>
                                    <TableCell size="small">Groupe</TableCell>
                                    <TableCell size="small">Organisateur</TableCell>
                                    <TableCell size="small">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupActivities.slice(0, limit).map((activity) => (
                                    <TableRow hover key={activity.uid} selected={selectedActivityIds.indexOf(activity.uid) !== -1}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedActivityIds.indexOf(activity.uid) !== -1}
                                                onChange={(event) => handleSelectOne(event, activity.uid)}
                                                value="true"
                                            />
                                        </TableCell>
                                        <TableCell size="small">{activity.name}</TableCell>
                                        <TableCell size="small">{day_translate.FR[activity.day]}</TableCell>
                                        <TableCell size="small">{moment(activity.startTime, "HH:mm:00").format("HH:mm")} - {moment(activity.endTime, "HH:mm:00").format("HH:mm")}</TableCell>
                                        <TableCell size="small">{activity.groupByGroupUid.name}</TableCell>
                                        <TableCell size="small">{activity.userByOrganizerUid ? (`${activity.userByOrganizerUid.firstName} ${activity.userByOrganizerUid.lastName}`) : ''}</TableCell>
                                        <TableCell size="small">
                                            <CustomIconButton
                                                title="Elèves"
                                                button={<Button
                                                    onClick={() => showUserListModal(activity.groupByGroupUid.uid)}
                                                    size="small"
                                                    variant="outlined"
                                                    color="default"
                                                    endIcon={<StudentIcon />}
                                                >{activity.groupByGroupUid?.userGroupsByGroupUid?.totalCount}</Button>} />
                                            <CustomIconButton
                                                onClick={() => showConflictModal(activity.uid)}
                                                title="Conflits"
                                                icon={<ConflictIcon />} />
                                            {!readOnly && <CustomIconButton
                                                onClick={() => { setActivityToUpdate(activity); setOpenEditModal(true); }}
                                                title="Modifier"
                                                icon={<EditIcon />} />}
                                            {!readOnly && <CustomIconButton
                                                onClick={() => openConfirmDelete(activity)}
                                                title="Supprimer"
                                                icon={<DeleteIcon />} />}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </PerfectScrollbar>
                <TablePagination
                    component="div"
                    count={totalCount}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleLimitChange}
                    page={page}
                    rowsPerPage={limit}
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                />
            </Card>
            <Card sx={{ display: { xs: 'block', lg: 'none' } }}>
                {!readOnly && selectedActivityIds.length > 0 && (
                    <Box sx={{ p: 2 }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            fullWidth
                            onClick={() => setOpenCopyDialog(true)}
                        >
                            {selectedActivityIds.length === totalCount 
                                ? `Copier toutes les options (${totalCount})`
                                : `Copier ${selectedActivityIds.length} option${selectedActivityIds.length > 1 ? 's' : ''}`
                            }
                        </Button>
                    </Box>
                )}
                <Table size="small">
                    <TableBody>
                        {groupActivities.slice(0, limit).map((activity) => (
                            <TableRow hover key={activity.uid} selected={selectedActivityIds.indexOf(activity.uid) !== -1}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedActivityIds.indexOf(activity.uid) !== -1}
                                        onChange={(event) => handleSelectOne(event, activity.uid)}
                                        value="true"
                                    />
                                </TableCell>
                                <TableCell>
                                    <strong>{activity.name}</strong><br />
                                    {day_translate.FR[activity.day]} : {moment(activity.startTime, "HH:mm:00").format("HH:mm")} - {moment(activity.endTime, "HH:mm:00").format("HH:mm")}<br />
                                    Places : {activity.taken} / {activity.capacity}<br />
                                    {activity.userByOrganizerUid ? (`Responsable : ${activity.userByOrganizerUid.firstName} ${activity.userByOrganizerUid.lastName}`) : ''}<br />
                                    Classe : {activity.groupByGroupUid.name}
                                </TableCell>
                                <TableCell>
                                    <CustomIconButton
                                        title="Elèves"
                                        button={<Button
                                            onClick={() => showUserListModal(activity.groupByGroupUid.uid)}
                                            size="small"
                                            variant="outlined"
                                            color="default"
                                            endIcon={<StudentIcon />}
                                        >{activity.groupByGroupUid?.userGroupsByGroupUid?.totalCount}</Button>} />
                                    <CustomIconButton
                                        onClick={() => showConflictModal(activity.uid)}
                                        title="Conflits"
                                        icon={<ConflictIcon />} />
                                    {!readOnly && <CustomIconButton
                                        onClick={() => { setActivityToUpdate(activity); setOpenEditModal(true); }}
                                        title="Modifier"
                                        icon={<EditIcon />} />}
                                    {!readOnly && <CustomIconButton
                                        onClick={() => openConfirmDelete(activity)}
                                        title="Supprimer"
                                        icon={<DeleteIcon />} />}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </div >
    );
};

GroupActivityListResults.propTypes = {
    groupActivities: PropTypes.array.isRequired
};

export default GroupActivityListResults;